import React, { useCallback, useEffect, useState } from 'react';
import { feedbackAPI } from '../services/api';
import {
  FiRefreshCw, FiPlus, FiCopy, FiCheck, FiX, FiSearch, FiStar, FiMessageCircle,
} from 'react-icons/fi';
import { FollowUpBox, InsightsPanel, ToSendPanel } from './FeedbackPanels';
import './Feedback.css';

const STATUS_TABS = [
  { key: 'COMPLETED', label: 'Answered' },
  { key: 'PENDING', label: 'Awaiting reply' },
  { key: 'EXPIRED', label: 'Expired' },
  { key: '', label: 'All' },
];

const KINDS = [
  { key: 'STUDENT', label: 'Student' },
  { key: 'JOB', label: 'Job Seeker' },
  { key: 'TOURIST', label: 'Tourist' },
  { key: 'MEDICAL', label: 'Medical Tourism' },
];

const MOMENTS = [
  { key: 'END_REVIEW', label: 'End-of-journey review', hint: 'After the outcome. About 4 minutes.' },
  { key: 'CHECK_IN', label: 'Mid-journey check-in', hint: 'After they commit. About 1 minute.' },
];

const NPS_LABEL = { PROMOTER: 'Promoter', PASSIVE: 'Passive', DETRACTOR: 'Detractor' };

const LEADERSHIP_ONLY = 'Answers are read only by the Chairman, CEO, COO and CSM.';

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function Stars({ value }) {
  if (value == null) return <span className="fb-muted">—</span>;
  return (
    <span className="fb-stars" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FiStar key={n} className={n <= value ? 'on' : ''} />
      ))}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="fb-btn"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        });
      }}
    >
      {copied ? <FiCheck /> : <FiCopy />} {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

function ShareLink({ item }) {
  return (
    <div className="fb-share">
      <div className="fb-share-url">{item.link}</div>
      <div className="fb-share-actions">
        <CopyButton text={item.link} />
        {item.whatsapp_url && (
          <a className="fb-btn fb-btn-wa" href={item.whatsapp_url} target="_blank" rel="noreferrer">
            <FiMessageCircle /> Send on WhatsApp
          </a>
        )}
      </div>
      {!item.whatsapp_url && item.status === 'PENDING' && (
        <p className="fb-muted">No phone number on file — copy the link and send it yourself.</p>
      )}
    </div>
  );
}

function NewLinkPanel({ onClose, onCreated }) {
  const [kind, setKind] = useState('STUDENT');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [role, setRole] = useState('CLIENT');
  const [moment, setMoment] = useState('END_REVIEW');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (client || query.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      feedbackAPI.searchClients({ client_kind: kind, q: query.trim() })
        .then(({ data }) => setResults(data || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [kind, query, client]);

  useEffect(() => {
    if (role === 'CLIENT' && client) {
      setName(client.name);
      setPhone(client.phone || '');
    } else if (role === 'SPONSOR') {
      setName('');
      setPhone('');
    }
  }, [role, client]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!client) { setError('Pick the client first.'); return; }
    if (role === 'SPONSOR' && !name.trim()) { setError("Enter the sponsor or parent's name."); return; }
    setBusy(true);
    try {
      const { data } = await feedbackAPI.create({
        client_kind: kind, client_id: client.id, respondent_role: role, moment,
        respondent_name: name.trim(), respondent_phone: phone.trim(),
      });
      setCreated(data);
      onCreated();
    } catch (err) {
      const d = err?.response?.data || {};
      setError(d.detail || Object.values(d).flat()[0] || 'Could not create the link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fb-overlay" role="dialog" aria-modal="true">
      <div className="fb-modal">
        <div className="fb-modal-head">
          <h2>{created ? 'Survey link ready' : 'New feedback survey'}</h2>
          <button type="button" className="fb-icon-btn" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        {created ? (
          <>
            <p className="fb-muted">
              For <strong>{created.respondent_name || created.client_name}</strong>
              {created.respondent_role === 'SPONSOR' && <> (sponsor/parent of {created.client_name})</>}.
              {' '}{created.moment_display}. The link works once and expires in 30 days.
            </p>
            <ShareLink item={created} />
            <div className="fb-modal-actions">
              <button type="button" className="fb-btn fb-btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="fb-form">
            <label className="fb-label">
              Client type
              <select value={kind} onChange={(e) => { setKind(e.target.value); setClient(null); setQuery(''); }}>
                {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </label>

            <label className="fb-label">
              Client
              {client ? (
                <div className="fb-picked">
                  <span><strong>{client.name}</strong> <span className="fb-muted">{client.ref}</span></span>
                  <button type="button" className="fb-link" onClick={() => { setClient(null); setQuery(''); }}>Change</button>
                </div>
              ) : (
                <div className="fb-search">
                  <FiSearch />
                  <input
                    value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, phone, email or ID" autoFocus
                  />
                </div>
              )}
              {!client && results.length > 0 && (
                <ul className="fb-results">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => setClient(r)}>
                        <strong>{r.name}</strong> <span className="fb-muted">{r.ref} {r.phone && `· ${r.phone}`}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!client && query.trim().length >= 2 && results.length === 0 && (
                <span className="fb-muted">No matching clients.</span>
              )}
            </label>

            <div className="fb-label">
              Who will answer?
              <div className="fb-seg">
                <button type="button" className={role === 'CLIENT' ? 'active' : ''} onClick={() => setRole('CLIENT')}>
                  The client
                </button>
                <button type="button" className={role === 'SPONSOR' ? 'active' : ''} onClick={() => setRole('SPONSOR')}>
                  Sponsor / parent
                </button>
              </div>
            </div>

            <div className="fb-label">
              Which survey?
              <div className="fb-seg">
                {MOMENTS.map((m) => (
                  <button key={m.key} type="button" className={moment === m.key ? 'active' : ''} onClick={() => setMoment(m.key)}>
                    {m.label}
                  </button>
                ))}
              </div>
              <span className="fb-muted">{MOMENTS.find((m) => m.key === moment).hint}</span>
            </div>

            <div className="fb-row">
              <label className="fb-label">
                {role === 'SPONSOR' ? "Sponsor / parent's name" : 'Name'}
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
              </label>
              <label className="fb-label">
                WhatsApp number
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+263…" />
              </label>
            </div>

            {error && <div className="fb-error">{error}</div>}
            <div className="fb-modal-actions">
              <button type="button" className="fb-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="fb-btn fb-btn-primary" disabled={busy}>
                {busy ? 'Creating…' : 'Create survey link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Feedback() {
  const [statusFilter, setStatusFilter] = useState('COMPLETED');
  const [kindFilter, setKindFilter] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (kindFilter) params.client_kind = kindFilter;
      if (search.trim()) params.search = search.trim();
      const [list, sum] = await Promise.all([
        feedbackAPI.list(params),
        feedbackAPI.summary(kindFilter ? { client_kind: kindFilter } : {}),
      ]);
      setItems(list.data.results || list.data);
      setSummary(sum.data);
    } catch {
      setError('Could not load feedback.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, kindFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const open = (item) => {
    setSelected(item);
    setNote(item.staff_note || '');
  };

  const saveReview = async (isReviewed) => {
    setBusy(true);
    try {
      const { data } = await feedbackAPI.markReviewed(selected.id, { is_reviewed: isReviewed, staff_note: note });
      setSelected(data);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      feedbackAPI.summary(kindFilter ? { client_kind: kindFilter } : {}).then(({ data: s }) => setSummary(s));
    } catch {
      alert('Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const stat = (label, value, hint) => (
    <div className="fb-stat">
      <div className="fb-stat-value">{value ?? '—'}</div>
      <div className="fb-stat-label">{label}</div>
      {hint && <div className="fb-stat-hint">{hint}</div>}
    </div>
  );

  return (
    <div className="fb">
      <div className="fb-header">
        <h1>Client Feedback</h1>
        <div className="fb-header-actions">
          <button type="button" className="fb-btn" onClick={load} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button type="button" className="fb-btn fb-btn-primary" onClick={() => setShowNew(true)}>
            <FiPlus /> New survey link
          </button>
        </div>
      </div>

      {summary && summary.leadership && (
        <div className="fb-stats">
          {stat('Answered', summary.answered)}
          {stat('Urgent', summary.urgent, 'not yet reviewed')}
          {stat('Follow-up needed', summary.follow_up, 'not yet reviewed')}
          {stat('Satisfaction', summary.avg_satisfaction != null ? `${summary.avg_satisfaction}/5` : null)}
          {stat('NPS score', summary.nps_score, `${summary.promoters} promoters · ${summary.detractors} detractors`)}
          {stat('Speed', summary.avg_speed != null ? `${summary.avg_speed}/5` : null)}
          {stat('Clarity', summary.avg_clarity != null ? `${summary.avg_clarity}/5` : null)}
          {stat('Awaiting reply', summary.awaiting)}
        </div>
      )}
      {summary && !summary.leadership && (
        <>
          <div className="fb-stats">
            {stat('Answered', summary.answered)}
            {stat('Awaiting reply', summary.awaiting)}
          </div>
          <p className="fb-muted fb-privacy">
            You see the survey links you sent and whether each was answered. {LEADERSHIP_ONLY}
          </p>
        </>
      )}

      <ToSendPanel onSent={load} />
      {summary && summary.leadership && <InsightsPanel clientKind={kindFilter} />}

      <div className="fb-filters">
        <div className="fb-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.key} type="button" className={statusFilter === t.key ? 'active' : ''}
              onClick={() => { setStatusFilter(t.key); setSelected(null); }}>
              {t.label}
            </button>
          ))}
        </div>
        <select value={kindFilter} onChange={(e) => { setKindFilter(e.target.value); setSelected(null); }}>
          <option value="">All client types</option>
          {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <div className="fb-search fb-search-inline">
          <FiSearch />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search names or IDs" />
        </div>
      </div>

      {error && <div className="fb-error">{error}</div>}

      <div className="fb-body">
        <div className="fb-list">
          {!loading && items.length === 0 && <div className="fb-empty">Nothing in this view yet.</div>}
          {items.map((it) => (
            <button key={it.id} type="button"
              className={`fb-rowcard ${selected?.id === it.id ? 'selected' : ''}`} onClick={() => open(it)}>
              <div className="fb-row-top">
                <span className="fb-name">{it.submitted_name || it.respondent_name || it.client_name}</span>
                {it.moment === 'CHECK_IN' && <span className="fb-chip fb-chip-moment">Check-in</span>}
                {it.moment === 'SETTLING' && <span className="fb-chip fb-chip-moment">Settling</span>}
                <span className={`fb-chip kind-${it.client_kind}`}>{it.client_kind_display}</span>
              </div>
              {it.respondent_role === 'SPONSOR' && (
                <div className="fb-sub">Sponsor/parent of {it.client_name}</div>
              )}
              <div className="fb-row-meta">
                {it.status === 'COMPLETED' && it.can_view_answers ? (
                  <>
                    <Stars value={it.satisfaction} />
                    {it.nps != null && <span className={`fb-nps nps-${it.nps_group}`}>{it.nps}/10</span>}
                    {it.alert_level !== 'NORMAL' && (
                      <span className={`fb-alert alert-${it.alert_level}`}>{it.alert_level_display}</span>
                    )}
                    {!it.is_reviewed && <span className="fb-new">New</span>}
                  </>
                ) : (
                  <span className={`fb-status st-${it.status}`}>{it.status_display}</span>
                )}
                <span className="fb-muted fb-time">{timeAgo(it.completed_at || it.created_at)}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="fb-detail">
          {!selected ? (
            <div className="fb-empty">Select a response to read it.</div>
          ) : (
            <>
              <h2>{selected.submitted_name || selected.respondent_name || selected.client_name}</h2>
              <div className="fb-facts">
                <div><span className="fb-muted">Client</span> {selected.client_name} {selected.client_ref && `(${selected.client_ref})`} · {selected.client_kind_display}</div>
                <div><span className="fb-muted">Survey</span> {selected.moment_display}</div>
                <div><span className="fb-muted">Answered by</span> {selected.respondent_role_display}</div>
                <div><span className="fb-muted">Sent by</span> {selected.created_by_name || '—'} · {new Date(selected.created_at).toLocaleDateString()}</div>
              </div>

              {selected.status === 'COMPLETED' && !selected.can_view_answers ? (
                <p className="fb-muted">
                  Answered on {new Date(selected.completed_at).toLocaleDateString()}. {LEADERSHIP_ONLY}
                </p>
              ) : selected.status === 'COMPLETED' ? (
                <>
                  {selected.alert_level !== 'NORMAL' && (
                    <div className={`fb-alert-box alert-${selected.alert_level}`}>
                      <strong>{selected.alert_level_display}</strong>
                      <ul>{selected.alert_reasons.map((r) => <li key={r}>{r}</li>)}</ul>
                    </div>
                  )}
                  {selected.questionnaire_version < 2 && <p className="fb-muted">Earlier format (original seven questions).</p>}
                  {selected.answer_sections.map((section) => (
                    <div key={section.title || 'answers'} className="fb-answer-section">
                      {section.title && <h3>{section.title}</h3>}
                      <dl className="fb-answers fb-answers-v2">
                        {section.items.map((a) => (
                          <div key={a.key}>
                            <dt>{a.label}</dt>
                            <dd>
                              {a.type === 'stars' && typeof a.value === 'number' ? <Stars value={a.value} /> : a.display}
                              {a.key === 'nps' && <span className="fb-muted"> {NPS_LABEL[selected.nps_group]}</span>}
                              {a.detail && <p className="fb-answer-detail">{a.detail}</p>}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}

                  <FollowUpBox item={selected} onClosed={(data) => {
                    setSelected(data);
                    setNote(data.staff_note || '');
                    setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
                  }} />

                  <label className="fb-label">
                    Follow-up note (internal)
                    <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Called them back, fixed the delay" />
                  </label>
                  <div className="fb-detail-actions">
                    {selected.is_reviewed ? (
                      <>
                        <span className="fb-reviewed"><FiCheck /> Reviewed by {selected.reviewed_by_name}</span>
                        <button type="button" className="fb-btn" disabled={busy} onClick={() => saveReview(true)}>Save note</button>
                        <button type="button" className="fb-btn" disabled={busy} onClick={() => saveReview(false)}>Mark not reviewed</button>
                      </>
                    ) : (
                      <button type="button" className="fb-btn fb-btn-primary" disabled={busy} onClick={() => saveReview(true)}>
                        <FiCheck /> Mark as reviewed
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="fb-muted">
                    {selected.status === 'PENDING'
                      ? 'Waiting for their answers. Resend the link if needed:'
                      : 'This link expired before it was answered. Create a new one to ask again.'}
                  </p>
                  {selected.status === 'PENDING' && <ShareLink item={selected} />}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showNew && <NewLinkPanel onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}
